// storage.js (FINAL VERSION - Flik-isolerad session)

import Logger from './logger.js';

// helper to save local storage
class Storage {
    static prefix = 'mscp_';

    static #workspace = null;
    static #workspaceId = null;

    static generateSessionId() {
        return Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
    }

    static getCurrentSessionId() {
        return this.#workspaceId;
    }

    // --- NEW PRIVATE METHODS FOR TAB-ISOLATION (SESSION STORAGE) ---

    static _getSessionPointerKey() {
        // Unique key for the pointer in sessionStorage
        return 'activeTabSessionId';
    }

    static _setSessionPointer(sessionId) {
        try {
            sessionStorage.setItem(this.prefix + this._getSessionPointerKey(), sessionId);
        } catch (error) {
            Logger.error("Error saving to sessionStorage pointer:", error);
        }
    }

    static _getSessionPointer() {
        try {
            return sessionStorage.getItem(this.prefix + this._getSessionPointerKey());
        } catch (error) {
            return null;
        }
    }

    // --- SESSIONS METHODS ---

    /**
     * Starts/resumes a session.
     * Also performs cleanup of old sessions.
     */
    static startSession() {
        this.cleanupOldSessions();

        // 1. FIRST: Check if this tab has an active, isolated session (sessionStorage)
        const tabSessionId = this._getSessionPointer();

        if (tabSessionId) {
            Logger.log(`Storage: Resuming isolated tab session ${tabSessionId}.`);
            const loaded = this.loadSession(tabSessionId); // Loads data from localStorage but uses the session pointer
            return { source: 'tab', loaded };
        }

        // 2. SECOND: Check if there is a recently saved global session (localStorage)
        const latestSessionId = this.findLatestCommittedSessionId() || this.getFromLocalStorage('currentSessionId');

        if (latestSessionId) {
            Logger.log(`Storage: Found latest saved/global session ${latestSessionId}. Cloning it.`);
            this.cloneSession(latestSessionId); // Clones data AND sets a new isolated pointer
            return { source: 'latest', loaded: true };
        } else {
            Logger.log(`Storage: No saved session found. Starting new isolated session.`);
            this.startNewSession();
            return { source: 'new', loaded: true };
        }
    }

    /**
     * Starts a completely new, empty session (forced).
     */
    static startNewSession() {
        this.#workspace = {};
        this.#workspaceId = this.generateSessionId();
        Logger.log(`Storage: New isolated session started with ID: ${this.#workspaceId}`);

        // The pointer is saved in sessionStorage (tab-specific)
        this._setSessionPointer(this.#workspaceId);
        // We remove the old global pointer to avoid future conflicts
        this.removeFromLocalStorage('currentSessionId');
    }

    /**
     * Loads a specific session AND sets it as the 'activeTabSessionId' for the tab.
     */
    static loadSession(sessionId) {
        const data = this.getFromLocalStorage(sessionId); // Data is fetched from localStorage

        if (data && typeof data === 'object') {
            this.#workspace = data;
            this.#workspaceId = sessionId;
            Logger.log(`Storage: Session loaded from localStorage with ID: ${sessionId}`);

            // The pointer is saved in sessionStorage
            this._setSessionPointer(sessionId);
            return true;
        } else {
            Logger.debug(`Storage: Could not load session ${sessionId}. Starting empty session instead.`);
            this.#workspace = {};
            this.#workspaceId = sessionId;

            this._setSessionPointer(sessionId);
            return false;
        }
    }

    static async cloneSession(sessionId, isEdit = false, originalCartItemKey = null) {
        const data = this.getFromLocalStorage(sessionId); // Data is fetched from localStorage

        if (data && typeof data === 'object') {
            const newSessionId = this.generateSessionId();
            const clonedData = { ...data };

            // IMPORTANT: During edit mode, we keep the same image (activeImageID, file, etc.)
            // We will only clone/update files at the final save step (uploadAndSaveThumbnail)
            // This allows users to cancel without wasting storage space

            // Don't store cart edit flags in localStorage - they're handled at page level
            // Make sure we don't carry over these flags from previous sessions
            delete clonedData.isEdit;
            delete clonedData.originalCartItemKey;

            // Save cloned data to localStorage
            this.saveToLocalStorage(newSessionId, clonedData);

            // Set the isolated pointer in sessionStorage
            this._setSessionPointer(newSessionId);

            this.#workspace = clonedData;
            this.#workspaceId = newSessionId;
            Logger.log(`Storage: Session ${sessionId} cloned to new isolated session ID: ${newSessionId}${isEdit ? ' (EDIT MODE)' : ''}`);
            return newSessionId;
        } else {
            Logger.debug(`Storage: Could not clone session ${sessionId}. Starting new isolated session.`);
            this.startNewSession(); // Call startNewSession which sets the correct pointer
            return this.#workspaceId;
        }
    }

    /**
     * Creates a new session based on server data (e.g., from an edit from cart), and sets it as the active session for the tab.
     * Maps back field names that LocalData.get() changes.
     */
    static createSessionFromData(serverData) {
        const newSessionId = this.generateSessionId();
        const data = { ...serverData };

        // Map back field names (LocalData.get() changes names during serialization)
        if (data.product !== undefined && data.selectProduct === undefined) {
            data.selectProduct = data.product;
            delete data.product;
        }
        if (data.retouchComment !== undefined && data.retouch_comment === undefined) {
            data.retouch_comment = data.retouchComment;
            delete data.retouchComment;
        }

        // Remove fields that don't belong in the client session
        delete data.sessionID;
        delete data.mscp_instance;
        delete data.isEdit;
        delete data.originalCartItemKey;

        // Save to localStorage
        this.saveToLocalStorage(newSessionId, data);

        // Set the isolated pointer in sessionStorage
        this._setSessionPointer(newSessionId);

        this.#workspace = data;
        this.#workspaceId = newSessionId;
        Logger.log(`Storage: Session created from server data with ID: ${newSessionId}`);
        return newSessionId;
    }

    /**
     * Saves the active session (workspace) to localStorage.
     */
    static commitSession() {
        if (!this.#workspace || !this.#workspaceId) {
            Logger.debug("Storage: commitSession called without an active session.");
            return null;
        }
        if (Object.keys(this.#workspace).length === 0) {
            Logger.debug("Storage: commitSession called with an empty session. Nothing to save.");
            return null;
        }

        this.#workspace['lastModified'] = new Date().toISOString();

        this.saveToLocalStorage(this.#workspaceId, this.#workspace);
        Logger.log(`Storage: Session ${this.#workspaceId} saved to localStorage with timestamp.`);
    }

    /**
     * Aborts the session AND clears the 'activeTabSessionId' from the tab.
     */
    static abortSession() {
        this.#workspace = null;
        this.#workspaceId = null;

        this._setSessionPointer(null); // Clear the pointer from sessionStorage

        Logger.log("Storage: Session aborted, changes removed from memory and pointer cleared.");
    }

    // --- CORE METHODS (for #workspace) ---

    static save(key, value) {
        if (this.#workspace !== null) {
            if (value === undefined || value === null) {
                Logger.debug(`Storage.save (session ${this.#workspaceId}): Value for key '${key}' is undefined/null.`);
                return;
            }
            this.#workspace[key] = value;
            Logger.debug(`Storage (session ${this.#workspaceId}): saved ${key} =`, value);
        } else {
            Logger.debug(`Storage.save: No active session. Value for '${key}' was not saved.`);
        }
    }

    static get(key) {
        if (this.#workspace !== null) {
            if (Object.prototype.hasOwnProperty.call(this.#workspace, key)) {
                const value = this.#workspace[key];
                Logger.debug(`Storage (session ${this.#workspaceId}): retrieved ${key} from session.`, value);
                return value;
            }
        }
        // Fallback to global (e.g., if js_version is requested)
        return this.getFromLocalStorage(key);
    }

    static remove(key) {
        if (this.#workspace !== null) {
            if (Object.prototype.hasOwnProperty.call(this.#workspace, key)) {
                delete this.#workspace[key];
                Logger.debug(`Storage (session ${this.#workspaceId}): removed ${key} from session.`);
            }
        } else {
            Logger.debug(`Storage.remove: No active session. '${key}' was not removed.`);
        }
    }

    // --- INTERNAL "SAFE" METHODS (for localStorage) ---

    static saveToLocalStorage(key, value) {
        try {
            if (value === undefined || value === null) {
                Logger.debug("Storage.saveToLocalStorage: value for key %s is undefined or null, not saving", key);
                return;
            }
            key = this.prefix + key;

            if (typeof value === 'object') {
                value = JSON.stringify(value);
            }
            // No artificial size cap here. localStorage's real quota (~5 MB)
            // is enforced by the browser and surfaces as QuotaExceededError,
            // which the catch below handles. A previous `value.length > 5000`
            // guard silently dropped any session larger than 5 KB — which is
            // almost every session once an image is uploaded — so
            // commitSession() never reached localStorage and the customizer
            // lost the whole session on any reload.
            localStorage.setItem(key, value);
        }
        catch (error) {
            Logger.error("Error saving to localStorage for key %s:", key, error);
        }
    }

    static getFromLocalStorage(key) {
        key = this.prefix + key;
        try {
            const value = localStorage.getItem(key);
            if (value === null) {
                return null;
            }
            if (value.startsWith('{') || value.startsWith('[')) {
                return JSON.parse(value);

            }
            return value;
        }
        catch (error) {
            Logger.error('Error loading from localStorage:', key, error);
            return null;
        }
    }

    static removeFromLocalStorage(key) {
        key = this.prefix + key;
        try {
            localStorage.removeItem(key);
        }
        catch (error) {
            Logger.error('Error removing from localStorage:', error);
        }
    }


    // --- INTERNAL "SAFE" METHODS (for sessionStorage) ---

    static saveToSessionStorage(key, value) {
        try {
            if (value === undefined || value === null) {
                Logger.debug("Storage.saveToSessionStorage: value for key %s is undefined or null, not saving", key);
                return;
            }
            key = this.prefix + key;
            if (typeof value === 'object') {
                value = JSON.stringify(value);
            }
            sessionStorage.setItem(key, value);
        }
        catch (error) {
            Logger.error("Error saving to sessionStorage for key %s:", key, error);
        }
    }

    static getFromSessionStorage(key) {
        key = this.prefix + key;
        try {
            const value = sessionStorage.getItem(key);
            if (value === null) {
                return null;
            }
            if (value.startsWith('{') || value.startsWith('[')) {
                return JSON.parse(value);
            }
            return value;
        }
        catch (error) {
            Logger.error('Error loading from sessionStorage:', key, error);
            return null;
        }
    }

    static removeFromSessionStorage(key) {
        key = this.prefix + key;
        try {
            sessionStorage.removeItem(key);
        }
        catch (error) {
            Logger.error('Error removing from sessionStorage:', error);
        }
    }


    // --- HELPER METHODS ---

    /**
     * Helper method to find the latest session ID based on 'lastModified'.
     */
    static findLatestCommittedSessionId() {
        let latestId = null;
        let latestTimestamp = 0;

        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);

                // Exclude all pointers and special keys
                if (!key.startsWith(this.prefix) || key.endsWith(this._getSessionPointerKey())) {
                    continue;
                }

                const value = localStorage.getItem(key);
                if (!value) continue;

                try {
                    const data = JSON.parse(value);

                    if (data && data.lastModified) {
                        const modifiedDate = new Date(data.lastModified);
                        const timestamp = modifiedDate.getTime();

                        if (!isNaN(timestamp) && timestamp > latestTimestamp) {
                            latestTimestamp = timestamp;
                            // Remove the prefix to get the clean session ID
                            latestId = key.substring(this.prefix.length);
                        }
                    }
                } catch (parseError) {
                    // Ignore non-JSON
                }
            }
        } catch (error) {
            Logger.error("Storage: Error finding the latest committed session:", error);
        }

        return latestId;
    }

    /**
     * NEW FUNCTION: Cleans up old sessions from localStorage.
     * Looks for 'lastModified' and removes if older than 30 days.
     */
    static cleanupOldSessions() {
        Logger.debug("Storage: Running 30-day cleanup of old sessions...");
        const now = new Date();
        const daysInMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
        const keysToRemove = [];

        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);

                // Must start with our prefix
                if (!key.startsWith(this.prefix)) {
                    continue;
                }

                // Ignore "special keys" that are not sessions
                const specialKeys = [
                    this.prefix + 'currentSessionId', // Old pointer
                    this.prefix + 'js_version',
                    this.prefix + 'lastSave'
                ];
                if (specialKeys.includes(key)) {
                    continue;
                }

                // Fetch and attempt to parse the data
                const value = localStorage.getItem(key);
                if (!value) continue;

                try {
                    const data = JSON.parse(value);

                    // Check if it's a session object with our timestamp
                    if (data && data.lastModified) {
                        const modifiedDate = new Date(data.lastModified);

                        // Check if the date is valid
                        if (isNaN(modifiedDate.getTime())) {
                            continue;
                        }

                        const ageInMs = now.getTime() - modifiedDate.getTime();

                        // If older than 30 days, mark for removal
                        if (ageInMs > daysInMs) {
                            keysToRemove.push(key);
                        }
                    }
                } catch (parseError) {
                    // Not JSON, probably not a session we can assess. Leave it.
                }
            }

            // Perform the deletion
            keysToRemove.forEach(key => {
                localStorage.removeItem(key);
                Logger.debug(`Storage: ${key} raderad pga ålder > 30 dagar.`);
            });

            if (keysToRemove.length === 0) {
                Logger.debug("Storage: Inga gamla sessioner att rensa.");
            }

        } catch (error) {
            Logger.error("Storage: Fel vid rensning av gamla sessioner:", error);
        }
    }

    static async updateVersion() {
        const currentVersion = Number(window?.mscp?.js_version) || 1;
        const storedVersion = Number(this.getFromLocalStorage('js_version'));
        if (!storedVersion) {
            this.saveToLocalStorage('js_version', currentVersion);
            Logger.log(`Storage: Ingen tidigare version hittades. Sätter version till ${currentVersion}.`);
            if (typeof hj === 'function') { hj('event', `storage_version_set version: ${currentVersion}`); }
            return;
        }
        if (storedVersion !== currentVersion) {
            if (typeof hj === 'function') { hj('event', `storage_version_update all cleared from: ${storedVersion} to: ${currentVersion}`); }
            console.error(`Clear storage. Storage version changed from ${storedVersion} to ${currentVersion}`);
            this.clear(); // Clear everything
            this.saveToLocalStorage('js_version', currentVersion);
        }
    }

    static async clear(key) {
        if (key) {
            this.removeFromLocalStorage(key);
            this.removeFromSessionStorage(key);
        } else {
            this.abortSession(); // Clear the session + pointer in sessionStorage
            localStorage.clear(); // Clear EVERYTHING in localStorage
            sessionStorage.clear(); // Clear EVERYTHING in sessionStorage for the tab
        }
    }
}

// Persist the working session whenever the tab becomes hidden — backgrounded,
// app-switched, screen locked, or navigating away. visibilitychange is the
// last event that fires reliably on mobile/iOS (beforeunload/unload do not),
// so this is the safety net that keeps the customizer session alive when iOS
// discards a backgrounded tab or an in-app browser recycles its webview.
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            Storage.commitSession();
        }
    });
}

export default Storage;